import { AxiosMethods, QuotesV2Api } from '@setvi/shared/enums';
import { axiosHelper } from '@setvi/shared/services';

import type {
  QuoteV2Configuration,
  CreateQuoteV2Body,
  RenewQuoteV2Body,
  AddQuoteV2ProductsBody,
  AddQuoteV2FreehandBody,
  UpdateQuoteV2ItemPricingBody,
  UpdateQuoteV2ItemQuantityBody,
  UpdateQuoteV2ItemNoteBody,
  UpdateQuoteV2ItemLabelsBody,
  AddQuoteV2SubitemBody,
  CreateQuoteV2SectionBody,
  UpdateQuoteV2SectionBody,
  MoveQuoteV2ItemsBody
} from 'Services/Query/QuotesV2/Types';

export enum QuotesV2MutationKeys {
  UPDATE_CONFIGURATION = 'update-quote-v2-configuration',
  UPDATE_STATUS = 'update-quote-v2-status',
  DELETE_QUOTE = 'delete-quote-v2',
  CREATE_QUOTE = 'create-quote-v2',
  RENEW_QUOTE = 'renew-quote-v2',
  ADD_PRODUCTS = 'add-quote-v2-products',
  ADD_FREEHAND = 'add-quote-v2-freehand',
  UPDATE_ITEM_PRICING = 'update-quote-v2-item-pricing',
  UPDATE_ITEM_QUANTITY = 'update-quote-v2-item-quantity',
  UPDATE_ITEM_NOTE = 'update-quote-v2-item-note',
  UPDATE_ITEM_LABELS = 'update-quote-v2-item-labels',
  REFRESH_ITEM_PRICE = 'refresh-quote-v2-item-price',
  ADD_SUBITEM = 'add-quote-v2-subitem',
  DELETE_SUBITEM = 'delete-quote-v2-subitem',
  CREATE_SECTION = 'create-quote-v2-section',
  UPDATE_SECTION = 'update-quote-v2-section',
  DELETE_SECTION = 'delete-quote-v2-section',
  MOVE_ITEMS = 'move-quote-v2-items',
  DELETE_ITEM = 'delete-quote-v2-item',
  EXPORT_PDF = 'export-quote-v2-pdf',
  REFRESH_ALL_PRICES = 'refresh-quote-v2-all-prices',
  UPDATE_PREVIEW_INFO = 'update-quote-v2-preview-info',
  UPLOAD_PROFILE_IMAGE = 'upload-quote-v2-profile-image'
}

export const updateQuoteV2ConfigurationMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.UPDATE_CONFIGURATION],
  mutationFn: (body: Partial<QuoteV2Configuration>) =>
    axiosHelper({
      endpoint: QuotesV2Api.Configuration,
      method: AxiosMethods.PATCH,
      baseURL: process.env.REVOPS_API,
      body
    })
});

export const updateQuoteV2StatusMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.UPDATE_STATUS],
  mutationFn: ({ quoteId, statusId }: { quoteId: string; statusId: string }) =>
    axiosHelper({
      endpoint: QuotesV2Api.UpdateStatus.replace(':quoteId', quoteId),
      method: AxiosMethods.PUT,
      baseURL: process.env.REVOPS_API,
      body: { statusId }
    })
});

export const deleteQuoteV2Mutation = () => ({
  mutationKey: [QuotesV2MutationKeys.DELETE_QUOTE],
  mutationFn: ({ quoteId }: { quoteId: string }) =>
    axiosHelper({
      endpoint: QuotesV2Api.Delete.replace(':quoteId', quoteId),
      method: AxiosMethods.DELETE,
      baseURL: process.env.REVOPS_API
    })
});

export const createQuoteV2Mutation = () => ({
  mutationKey: [QuotesV2MutationKeys.CREATE_QUOTE],
  mutationFn: (body: CreateQuoteV2Body) =>
    axiosHelper({
      endpoint: QuotesV2Api.Create,
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    }),
  retry: false
});

export const renewQuoteV2Mutation = () => ({
  mutationKey: [QuotesV2MutationKeys.RENEW_QUOTE],
  mutationFn: ({ quoteId, ...body }: RenewQuoteV2Body & { quoteId: string }) =>
    axiosHelper({
      endpoint: QuotesV2Api.Renew.replace(':quoteId', quoteId),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    })
});

export const addQuoteV2ProductsMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.ADD_PRODUCTS],
  mutationFn: ({
    quoteId,
    ...body
  }: AddQuoteV2ProductsBody & { quoteId: string }) =>
    axiosHelper({
      endpoint: QuotesV2Api.AddProducts.replace(':quoteId', quoteId),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    })
});

export const addQuoteV2FreehandMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.ADD_FREEHAND],
  mutationFn: ({
    quoteId,
    ...body
  }: AddQuoteV2FreehandBody & { quoteId: string }) =>
    axiosHelper({
      endpoint: QuotesV2Api.AddFreehand.replace(':quoteId', quoteId),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    })
});

export const updateQuoteV2ItemPricingMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.UPDATE_ITEM_PRICING],
  mutationFn: ({
    quoteId,
    itemId,
    ...body
  }: UpdateQuoteV2ItemPricingBody & {
    quoteId: string;
    itemId: string;
  }) =>
    axiosHelper({
      endpoint: QuotesV2Api.UpdateItemPricing.replace(
        ':quoteId',
        quoteId
      ).replace(':itemId', itemId),
      method: AxiosMethods.PATCH,
      baseURL: process.env.REVOPS_API,
      body
    })
});

export const updateQuoteV2ItemQuantityMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.UPDATE_ITEM_QUANTITY],
  mutationFn: ({
    quoteId,
    itemId,
    value
  }: UpdateQuoteV2ItemQuantityBody & {
    quoteId: string;
    itemId: string;
  }) =>
    axiosHelper({
      endpoint: QuotesV2Api.UpdateItemQuantity.replace(
        ':quoteId',
        quoteId
      ).replace(':itemId', itemId),
      method: AxiosMethods.PATCH,
      baseURL: process.env.REVOPS_API,
      body: { value }
    })
});

export const updateQuoteV2ItemNoteMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.UPDATE_ITEM_NOTE],
  mutationFn: ({
    quoteId,
    itemId,
    note
  }: UpdateQuoteV2ItemNoteBody & {
    quoteId: string;
    itemId: string;
  }) =>
    axiosHelper({
      endpoint: QuotesV2Api.UpdateItemNote.replace(':quoteId', quoteId).replace(
        ':itemId',
        itemId
      ),
      method: AxiosMethods.PATCH,
      baseURL: process.env.REVOPS_API,
      body: { note }
    })
});

export const updateQuoteV2ItemLabelsMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.UPDATE_ITEM_LABELS],
  mutationFn: ({
    quoteId,
    itemId,
    ...body
  }: UpdateQuoteV2ItemLabelsBody & {
    quoteId: string;
    itemId: string;
  }) =>
    axiosHelper({
      endpoint: QuotesV2Api.UpdateItemLabels.replace(
        ':quoteId',
        quoteId
      ).replace(':itemId', itemId),
      method: AxiosMethods.PATCH,
      baseURL: process.env.REVOPS_API,
      body
    })
});

export const refreshQuoteV2ItemPriceMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.REFRESH_ITEM_PRICE],
  mutationFn: ({ quoteId, itemId }: { quoteId: string; itemId: string }) =>
    axiosHelper({
      endpoint: QuotesV2Api.RefreshItemPrice.replace(
        ':quoteId',
        quoteId
      ).replace(':itemId', itemId),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API
    })
});

export const addQuoteV2SubitemMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.ADD_SUBITEM],
  mutationFn: ({
    quoteId,
    itemId,
    sell,
    ...rest
  }: AddQuoteV2SubitemBody & {
    quoteId: string;
    itemId: string;
  }) =>
    axiosHelper({
      endpoint: QuotesV2Api.AddSubitem.replace(':quoteId', quoteId).replace(
        ':itemId',
        itemId
      ),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body: {
        ...rest,
        ...(sell ? { sell } : {})
      }
    })
});

export const deleteQuoteV2SubitemMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.DELETE_SUBITEM],
  mutationFn: ({
    quoteId,
    itemId,
    subId
  }: {
    quoteId: string;
    itemId: string;
    subId: string;
  }) =>
    axiosHelper({
      endpoint: QuotesV2Api.DeleteSubitem.replace(':quoteId', quoteId)
        .replace(':itemId', itemId)
        .replace(':subId', subId),
      method: AxiosMethods.DELETE,
      baseURL: process.env.REVOPS_API
    })
});

export const createQuoteV2SectionMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.CREATE_SECTION],
  mutationFn: ({
    quoteId,
    ...body
  }: CreateQuoteV2SectionBody & { quoteId: string }) =>
    axiosHelper({
      endpoint: QuotesV2Api.CreateSection.replace(':quoteId', quoteId),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    })
});

export const updateQuoteV2SectionMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.UPDATE_SECTION],
  mutationFn: ({
    quoteId,
    sectionId,
    ...body
  }: UpdateQuoteV2SectionBody & {
    quoteId: string;
    sectionId: string;
  }) =>
    axiosHelper({
      endpoint: QuotesV2Api.UpdateSection.replace(':quoteId', quoteId).replace(
        ':sectionId',
        sectionId
      ),
      method: AxiosMethods.PATCH,
      baseURL: process.env.REVOPS_API,
      body
    })
});

export const deleteQuoteV2SectionMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.DELETE_SECTION],
  mutationFn: ({
    quoteId,
    sectionId,
    keepItems = false
  }: {
    quoteId: string;
    sectionId: string;
    keepItems?: boolean;
  }) => {
    const base = QuotesV2Api.DeleteSection.replace(':quoteId', quoteId).replace(
      ':sectionId',
      sectionId
    );
    const endpoint = keepItems ? `${base}?keepItems=true` : base;

    return axiosHelper({
      endpoint,
      method: AxiosMethods.DELETE,
      baseURL: process.env.REVOPS_API
    });
  }
});

export const moveQuoteV2ItemsMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.MOVE_ITEMS],
  mutationFn: ({
    quoteId,
    ...body
  }: MoveQuoteV2ItemsBody & { quoteId: string }) =>
    axiosHelper({
      endpoint: QuotesV2Api.MoveItems.replace(':quoteId', quoteId),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    })
});

export const exportQuoteV2PDFMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.EXPORT_PDF],
  mutationFn: ({
    quoteId,
    ...rest
  }: {
    quoteId: string;
    showManufacturerDetails?: boolean;
  }) =>
    axiosHelper({
      endpoint: QuotesV2Api.ExportPDF.replace(':quoteId', quoteId),
      method: AxiosMethods.GET,
      params: rest,
      baseURL: process.env.REVOPS_API,
      responseType: 'blob',
      headers: {
        Accept: 'text/plain',
        'content-disposition': 'attachment'
      }
    })
});

export const refreshAllQuoteV2PricesMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.REFRESH_ALL_PRICES],
  mutationFn: ({ quoteId }: { quoteId: string }) =>
    axiosHelper({
      endpoint: QuotesV2Api.RefreshAllPrices.replace(':quoteId', quoteId),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API
    })
});

export const updateQuoteV2PreviewInfoMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.UPDATE_PREVIEW_INFO],
  mutationFn: ({
    quoteId,
    ...body
  }: {
    quoteId: string;
    preparedFor?: {
      companyPhoneNumber: string;
      companyAddress: string;
      companyName: string;
    };
    preparedBy?: {
      usedForOtherSalesRep?: boolean;
      companyRole?: string;
      email: string;
      phoneNumber: string;
      lastName: string;
      firstName: string;
      fullName: string;
      photo: string;
    };
    showManufacturerDetails?: boolean;
    validUntil?: string;
    customerNote?: string;
  }) =>
    axiosHelper({
      endpoint: QuotesV2Api.UpdatePreviewInfo.replace(':quoteId', quoteId),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    })
});

export const uploadQuoteV2ProfileImageMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.UPLOAD_PROFILE_IMAGE],
  mutationFn: ({ quoteId, image }: { quoteId: string; image: File }) => {
    const formData = new FormData();
    formData.append('image', image);

    return axiosHelper({
      endpoint: QuotesV2Api.UploadProfileImage.replace(':quoteId', quoteId),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
});

export const deleteQuoteV2ItemMutation = () => ({
  mutationKey: [QuotesV2MutationKeys.DELETE_ITEM],
  mutationFn: ({ quoteId, itemId }: { quoteId: string; itemId: string }) =>
    axiosHelper({
      endpoint: QuotesV2Api.DeleteItem.replace(':quoteId', quoteId).replace(
        ':itemId',
        itemId
      ),
      method: AxiosMethods.DELETE,
      baseURL: process.env.REVOPS_API
    })
});
